import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createNpiPhysician } from "../../../services/physicianService";
import { formatPhone } from "../../Application/application.validation";
import { PATIENT_THEME } from "../patientTheme";
import { PECOS_STATUS_OPTIONS } from "../patientForm.shared";

const fieldClass = `block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none ${PATIENT_THEME.focus}`;
const labelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";

/**
 * Modal to add a missing physician into the shared NPI catalog.
 */
const AddPhysicianModal = ({
  isOpen,
  onClose,
  onCreated,
  initialName = "",
  initialNpi = "",
}) => {
  const [npi, setNpi] = useState("");
  const [entityType, setEntityType] = useState("1");
  const [pecosStatus, setPecosStatus] = useState("un-enrolled");
  const [organizationName, setOrganizationName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [credential, setCredential] = useState("");
  const [taxonomyCode, setTaxonomyCode] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [fax, setFax] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const seedName = String(initialName || "").trim();
    const seedNpi = String(initialNpi || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    setNpi(seedNpi);
    setEntityType("1");
    setPecosStatus("un-enrolled");
    setOrganizationName("");
    setFirstName("");
    setMiddleName("");
    setLastName(/^\d+$/.test(seedName) ? "" : seedName);
    setCredential("");
    setTaxonomyCode("");
    setSpecialty("");
    setAddress("");
    setCity("");
    setState("");
    setZip("");
    setPhone("");
    setFax("");
    setErrors({});
    setSaving(false);
  }, [isOpen, initialName, initialNpi]);

  if (!isOpen) return null;

  const validate = () => {
    const next = {};
    if (!/^\d{10}$/.test(String(npi).trim())) {
      next.npi = "NPI must be exactly 10 digits";
    }
    if (entityType === "2") {
      if (!String(organizationName).trim()) {
        next.organizationName = "Organization name is required";
      }
    } else if (
      !String(firstName).trim() &&
      !String(lastName).trim()
    ) {
      next.lastName = "First or last name is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const res = await createNpiPhysician({
        npi: npi.trim(),
        entityType,
        pecosStatus:
          pecosStatus === "enrolled" || pecosStatus === "un-enrolled"
            ? pecosStatus
            : "un-enrolled",
        organizationName: organizationName.trim() || null,
        firstName: firstName.trim() || null,
        middleName: middleName.trim() || null,
        lastName: lastName.trim() || null,
        credential: credential.trim() || null,
        taxonomyCode: taxonomyCode.trim() || null,
        specialty: specialty.trim() || null,
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        zip: zip.trim() || null,
        phone: phone.trim() || null,
        fax: fax.trim() || null,
      });
      const payload = res.result || res;
      const physician = payload.physician || payload;
      toast.success("Physician added to catalog");
      onCreated?.(physician);
      onClose?.();
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        "Failed to add physician. Please try again.";
      toast.error(message);
      if (/already exists/i.test(message)) {
        setErrors((prev) => ({ ...prev, npi: message }));
      }
    } finally {
      setSaving(false);
    }
  };

  const pecosField = (
    <div className="space-y-1">
      <label className={labelClass}>PECOS Status</label>
      <select
        className={fieldClass}
        value={pecosStatus}
        onChange={(e) => setPecosStatus(e.target.value)}
      >
        {PECOS_STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-physician-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className={`px-4 py-3 border-b sticky top-0 ${PATIENT_THEME.modalHead}`}>
          <h2
            id="add-physician-title"
            className={`text-sm font-semibold uppercase tracking-wide ${PATIENT_THEME.modalTitle}`}
          >
            Add Physician
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-600">
            Add a new NPI to the shared catalog for all users.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>
                NPI <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={fieldClass}
                value={npi}
                maxLength={10}
                placeholder="10-digit NPI"
                autoFocus
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
              />
              {errors.npi ? (
                <p className="text-[11px] text-red-600">{errors.npi}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Entity Type <span className="text-red-500">*</span>
              </label>
              <select
                className={fieldClass}
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
              >
                <option value="1">Individual (1)</option>
                <option value="2">Organization (2)</option>
              </select>
            </div>
          </div>

          {entityType === "2" ? (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {pecosField}
              <div className="space-y-1 sm:col-span-3">
                <label className={labelClass}>
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={fieldClass}
                  value={organizationName}
                  maxLength={255}
                  onChange={(e) => setOrganizationName(e.target.value)}
                />
                {errors.organizationName ? (
                  <p className="text-[11px] text-red-600">{errors.organizationName}</p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {pecosField}
              <div className="space-y-1">
                <label className={labelClass}>First Name</label>
                <input
                  type="text"
                  className={fieldClass}
                  value={firstName}
                  maxLength={100}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className={labelClass}>Middle Name</label>
                <input
                  type="text"
                  className={fieldClass}
                  value={middleName}
                  maxLength={100}
                  onChange={(e) => setMiddleName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className={labelClass}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={fieldClass}
                  value={lastName}
                  maxLength={100}
                  onChange={(e) => setLastName(e.target.value)}
                />
                {errors.lastName ? (
                  <p className="text-[11px] text-red-600">{errors.lastName}</p>
                ) : null}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>Credential</label>
              <input
                type="text"
                className={fieldClass}
                value={credential}
                maxLength={50}
                placeholder="MD, DO, NP…"
                onChange={(e) => setCredential(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>Specialty</label>
              <input
                type="text"
                className={fieldClass}
                value={specialty}
                maxLength={255}
                onChange={(e) => setSpecialty(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>Taxonomy Code</label>
              <input
                type="text"
                className={fieldClass}
                value={taxonomyCode}
                maxLength={20}
                onChange={(e) => setTaxonomyCode(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>Phone</label>
              <input
                type="text"
                className={fieldClass}
                value={phone}
                maxLength={13}
                placeholder="(XXX)XXX-XXXX"
                onChange={(e) => setPhone(formatPhone(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>Fax</label>
              <input
                type="text"
                className={fieldClass}
                value={fax}
                maxLength={13}
                placeholder="(XXX)XXX-XXXX"
                onChange={(e) => setFax(formatPhone(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className={labelClass}>Practice Address</label>
            <input
              type="text"
              className={fieldClass}
              value={address}
              maxLength={500}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-1">
              <label className={labelClass}>City</label>
              <input
                type="text"
                className={fieldClass}
                value={city}
                maxLength={100}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>State</label>
              <input
                type="text"
                className={fieldClass}
                value={state}
                maxLength={2}
                placeholder="CA"
                onChange={(e) => setState(e.target.value.toUpperCase())}
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>ZIP</label>
              <input
                type="text"
                className={fieldClass}
                value={zip}
                maxLength={10}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`h-9 px-3 text-sm font-semibold rounded-md disabled:opacity-50 inline-flex items-center gap-2 ${PATIENT_THEME.button.primary}`}
            >
              {saving ? (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              Save Physician
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPhysicianModal;
